import React from 'react';
import { 
  Undo2, 
  Redo2, 
  Copy, 
  Trash2, 
  ArrowUpToLine, 
  ArrowDownToLine, 
  ArrowUp, 
  ArrowDown, 
  ZoomIn, 
  ZoomOut, 
  Save, 
  ArrowLeft,
  FileText,
  Keyboard
} from 'lucide-react';
import { DocumentoModelo } from '../../types/documentoModelo';

interface Props {
  modelo: DocumentoModelo;
  onUpdateModeloName: (nome: string) => void;
  onUndo: () => void;
  onRedo: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onBringToFront: () => void;
  onSendToBack: () => void;
  onBringForward: () => void;
  onSendBackward: () => void;
  zoomLevel: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onResetZoom: () => void;
  onSave: () => void;
  onClose: () => void;
  onOpenShortcutsModal: () => void;
  isSaving?: boolean;
  hasUnsavedChanges?: boolean;
}

export const ToolbarEditor: React.FC<Props> = ({
  modelo,
  onUpdateModeloName,
  onUndo,
  onRedo,
  onDuplicate,
  onDelete,
  onBringToFront,
  onSendToBack,
  onBringForward,
  onSendBackward,
  zoomLevel,
  onZoomIn,
  onZoomOut,
  onResetZoom,
  onSave,
  onClose,
  onOpenShortcutsModal,
  isSaving = false,
  hasUnsavedChanges = false,
}) => {
  return (
    <div className="h-16 bg-slate-900 text-white border-b border-slate-800 px-4 flex items-center justify-between shrink-0 select-none shadow-md">
      {/* Esquerda: Voltar + Nome do Modelo */}
      <div className="flex items-center gap-3">
        <button
          onClick={onClose}
          className="p-2 rounded-xl hover:bg-slate-800 text-slate-300 hover:text-white transition flex items-center gap-1.5 text-xs font-bold"
          title="Voltar aos modelos"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="hidden md:inline">Voltar</span>
        </button>

        <div className="h-6 w-px bg-slate-800" />

        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-red-600/20 text-red-500 border border-red-500/30">
            <FileText className="w-4 h-4" />
          </div>
          <div>
            <input
              type="text"
              value={modelo.nome}
              onChange={(e) => onUpdateModeloName(e.target.value)}
              className="bg-transparent text-sm font-bold text-white border-b border-transparent hover:border-slate-700 focus:border-red-500 focus:bg-slate-800/80 px-1.5 py-0.5 rounded outline-none transition w-56 md:w-80"
              placeholder="Nome do Modelo..."
            />
            <div className="text-[10px] text-slate-400 px-1.5 flex items-center gap-2">
              <span className="uppercase font-semibold tracking-wider text-red-400">{modelo.tipo.replace('_', ' ')}</span>
              <span>•</span>
              <span>Versão {modelo.versao}</span>
              {hasUnsavedChanges && (
                <>
                  <span>•</span>
                  <span className="text-amber-400 font-medium">Alterações não salvas</span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Centro: Ferramentas de Edição (Undo/Redo, Camadas, Duplicar, Excluir, Atalhos) */}
      <div className="hidden lg:flex items-center gap-1 bg-slate-800/80 p-1 rounded-xl border border-slate-700/60">
        {/* Undo / Redo */}
        <button
          onClick={onUndo}
          className="p-2 rounded-lg hover:bg-slate-700 text-slate-300 hover:text-white transition"
          title="Desfazer (Ctrl+Z)"
        >
          <Undo2 className="w-4 h-4" />
        </button>
        <button
          onClick={onRedo}
          className="p-2 rounded-lg hover:bg-slate-700 text-slate-300 hover:text-white transition"
          title="Refazer (Ctrl+Y)"
        >
          <Redo2 className="w-4 h-4" />
        </button>

        <div className="h-4 w-px bg-slate-700 mx-1" />

        {/* Layer Controls */}
        <button
          onClick={onBringToFront}
          className="p-2 rounded-lg hover:bg-slate-700 text-slate-300 hover:text-white transition"
          title="Trazer para Frente Total"
        >
          <ArrowUpToLine className="w-4 h-4" />
        </button>
        <button
          onClick={onBringForward}
          className="p-2 rounded-lg hover:bg-slate-700 text-slate-300 hover:text-white transition"
          title="Avançar uma Camada"
        >
          <ArrowUp className="w-4 h-4" />
        </button>
        <button
          onClick={onSendBackward}
          className="p-2 rounded-lg hover:bg-slate-700 text-slate-300 hover:text-white transition"
          title="Recuar uma Camada"
        >
          <ArrowDown className="w-4 h-4" />
        </button>
        <button
          onClick={onSendToBack}
          className="p-2 rounded-lg hover:bg-slate-700 text-slate-300 hover:text-white transition"
          title="Enviar para Trás Total"
        >
          <ArrowDownToLine className="w-4 h-4" />
        </button>

        <div className="h-4 w-px bg-slate-700 mx-1" />

        {/* Duplicate / Delete */}
        <button
          onClick={onDuplicate}
          className="p-2 rounded-lg hover:bg-slate-700 text-slate-300 hover:text-white transition"
          title="Duplicar Selecionado (Ctrl+D)"
        >
          <Copy className="w-4 h-4" />
        </button>
        <button
          onClick={onDelete}
          className="p-2 rounded-lg hover:bg-red-500/20 text-rose-400 hover:text-rose-300 transition"
          title="Excluir Selecionado (Del)"
        >
          <Trash2 className="w-4 h-4" />
        </button>

        <div className="h-4 w-px bg-slate-700 mx-1" />

        {/* Keyboard Shortcuts Button */}
        <button
          onClick={onOpenShortcutsModal}
          className="px-2.5 py-1.5 rounded-lg hover:bg-slate-700 text-slate-300 hover:text-white transition flex items-center gap-1.5 text-xs font-medium"
          title="Ver Atalhos do Teclado"
        >
          <Keyboard className="w-4 h-4 text-amber-400" />
          <span>Atalhos</span>
        </button>
      </div>

      {/* Direita: Zoom & Salvar */}
      <div className="flex items-center gap-3">
        {/* Zoom */}
        <div className="flex items-center gap-1 bg-slate-800/80 p-1 rounded-xl border border-slate-700/60 text-xs">
          <button
            onClick={onZoomOut}
            className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-300 hover:text-white transition"
            title="Reduzir Zoom (Ctrl + -)"
          >
            <ZoomOut className="w-3.5 h-3.5" />
          </button>

          <button
            onClick={onResetZoom}
            className="px-2 font-mono text-[11px] font-bold text-slate-300 hover:text-white"
            title="Resetar Zoom (Ctrl + 0)"
          >
            {Math.round(zoomLevel * 100)}%
          </button>

          <button
            onClick={onZoomIn}
            className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-300 hover:text-white transition"
            title="Aumentar Zoom (Ctrl + +)"
          >
            <ZoomIn className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Salvar Button */}
        <button
          onClick={onSave}
          disabled={isSaving}
          className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-bold flex items-center gap-2 shadow-md shadow-red-600/30 transition active:scale-95 disabled:opacity-50"
          title="Salvar Modelo (Ctrl+S)"
        >
          <Save className="w-4 h-4" />
          <span>{isSaving ? 'Salvando...' : 'Salvar Modelo'}</span>
        </button>
      </div>
    </div>
  );
};
